import { useEffect } from "react";
import { Badge, Tooltip } from "antd";
import { CloudDownloadOutlined, CloudUploadOutlined } from "@ant-design/icons";
import cx from "classnames";
import { get as localDbGet } from "idb-keyval";
import { CloudWatchLogger } from "helpers/cloudWatchLogger";
import { VERSION_NUMBER } from "config";
import { getBrowserDetails } from "helpers/browserInformation";
import "../AppHeader.scss";
import { useEntityData } from "EntityDataContext/EntityDataContext";
import { useOnlineContext } from "helpers/contexts/OnlineContext/OnlineContext";

const status = ({
  isSyncingData,
  imagesToUploadCount,
  imagesToDownloadCount,
  outboxData,
  isOnline,
  downloadAllImages,
}) => {
  const statusIndicator = (type, length) => (
    <Badge count={length}>
      <Tooltip title={type} placement="top">
        <div className="status-indicator"></div>
      </Tooltip>
    </Badge>
  );

  const OfflineStatusItemCounter = ({count}) => (
    <div className="offline-status-item-counter">
      <span>{count !== 0 ? count : null}</span>
    </div>
  );

  if (!isOnline && !isSyncingData) {
    return (
      <OfflineStatusItemCounter count={outboxData.length + imagesToUploadCount} />
    );
  }

  if (isOnline && !downloadAllImages) {
    return (
      <>
        {statusIndicator("Online", outboxData.length + imagesToUploadCount)}
         {(outboxData.length > 0 || imagesToUploadCount > 0) && (
          <>
            <CloudUploadOutlined className="cloudPulse" />
            <span>Items: {outboxData.length} </span>
            <span>Images: {imagesToUploadCount}</span>
          </>
        )}
      </>
      )
  }

  if ((isSyncingData || isOnline) && (outboxData.length > 0 || imagesToUploadCount > 0 || imagesToDownloadCount > 0)) {
    return (
      <>
        {statusIndicator("Online", (outboxData.length + imagesToUploadCount))}
        {(outboxData.length > 0 || imagesToUploadCount > 0) && (
          <>
            <CloudUploadOutlined className="cloudPulse" />
            <span>Items: {outboxData.length} </span>
            <span>Images: {imagesToUploadCount}</span>
          </>
        )}
        {imagesToDownloadCount > 0 && (
          <>
            <CloudDownloadOutlined className="cloudPulse" />
            <span>images: {imagesToDownloadCount}</span>
          </>
        )}
      </>
    )
  }

  return statusIndicator("Online", outboxData.length);
};

export default function OnlineStatusIndicator() {
  const {
    syncStatusHook: {
      isOutboxEmpty,
      isSyncingData,
      imagesToUploadCount,
      imagesToDownloadCount,
      outboxData
    },
    downloadAllImages,
    userData
  } = useEntityData()

  const {
    isOnline,
    onDataStoreSync
  } = useOnlineContext()

  useEffect(() => {
    const createLog = async () => {
      const { env } = JSON.parse(await localDbGet("get-tenant-data"))
      const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = window?.performance?.memory

      const { email } = userData

      const reportData = {
        inspectionsVersion: `${VERSION_NUMBER ? VERSION_NUMBER : "localhost"}`,
        user: email,
        device: getBrowserDetails(),
        performance: {
          usedJSHeapSize,
          totalJSHeapSize,
          jsHeapSizeLimit
        },
        imageOutboxSize: imagesToUploadCount
      }

      setTimeout(() => {
        CloudWatchLogger(
        `/aws/lambda/rest-${env}`,
        "info",
        `${email} is online`,
        reportData
      )
      }, 2000) // 2 second delay being added to ensure that the newly connected state is stable before sending online message
    }

    if (isOnline) {
      createLog()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isOnline ])

  useEffect(() => {
    if (isSyncingData && !isOnline) {
      onDataStoreSync()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSyncingData])

  return (
    <div
      className={cx("online-status", {
        online: isOnline,
        offline: !isOnline
      })}
    >
      {status({
        isOutboxEmpty,
        isSyncingData,
        imagesToUploadCount,
        imagesToDownloadCount,
        outboxData,
        isOnline,
        downloadAllImages,
      })}
    </div>
  );
}
